import type { Project, SurveyAnswer, SurveyQuestion, SurveyRoundResults, SurveySubmission } from '../types'
import { yearTotal, yearToDateTotal, monthTotal, monthKey, MONTH_NAMES } from './ledger'
import { riskScore, riskRag, RISK_LEVEL_LABEL, RAG_LABEL } from './risk'
import { partnerName, placeName, slotOf } from './surveyResults'

function esc(val: unknown): string {
  if (val === null || val === undefined) return ''
  const s = String(val)
  // A lone carriage return would end the row too, so it is quoted as well.
  return s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')
    ? `"${s.replace(/"/g, '""')}"`
    : s
}

export function row(cols: unknown[]): string {
  return cols.map(esc).join(',')
}

function download(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// Forecast projects don't have a seeded ledger yet (the ledger is auto-seeded
// from expectedValue only when status flips to 'complete'). For target columns,
// fall back to the per-project expectedValue so pipeline projects still show
// their planned hours instead of a misleading zero.
function targetForYear(project: Project, year: number): number {
  const ledgerSum = yearTotal(project.monthlyLedger, year)
  if (ledgerSum > 0) return ledgerSum
  if (project.status === 'forecast' && project.expectedValue) return project.expectedValue
  return 0
}

export function exportProjectsCsv(projects: Project[]) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // 1-12
  const lastYear = currentYear - 1
  const nextYear = currentYear + 1

  const headers = [
    'Project', 'Sub-Portfolio', 'Tier', 'Status',
    'Due Date', 'Actual Go-Live',
    `${lastYear} Realised`,
    `${currentYear} YTD Realised`,
    `${currentYear} Target`,
    `${nextYear} Target`,
  ]

  const rows = projects.map(p => {
    // Realised columns: only completed projects can have realised hours. The
    // ledger seeds on completion, so a forecast project's ledger is normally
    // empty, but exclude it explicitly so any pre-completion adjustments
    // don't sneak into a "realised" column. This keeps the export's realised
    // total aligned with the Reports headline.
    const isComplete = p.status === 'complete'
    return row([
      p.name,
      p.subPortfolio,
      p.tier,
      p.status,
      p.dueDate ?? '',
      p.actualGoLive ?? '',
      isComplete ? yearTotal(p.monthlyLedger, lastYear) : 0,
      isComplete ? yearToDateTotal(p.monthlyLedger, currentYear, currentMonth) : 0,
      targetForYear(p, currentYear),
      targetForYear(p, nextYear),
    ])
  })

  const csv = [row(headers), ...rows].join('\n')
  download(csv, `KPI-Hours-Saved-${now.toISOString().split('T')[0]}.csv`)
}

export function exportAllDataCsv(projects: Project[]) {
  const now = new Date()
  const dateStr = now.toISOString().split('T')[0]

  // Collect all ledger years that exist across all projects so we can build
  // dynamic month columns covering the full data range.
  const yearSet = new Set<number>()
  for (const p of projects) {
    for (const key of Object.keys(p.monthlyLedger || {})) {
      const y = Number(key.slice(0, 4))
      if (!Number.isNaN(y)) yearSet.add(y)
    }
  }
  const ledgerYears = Array.from(yearSet).sort((a, b) => a - b)

  // One column per month per year: "Jan 2025", "Feb 2025", ...
  const monthHeaders: string[] = []
  for (const year of ledgerYears) {
    for (let m = 0; m < 12; m++) {
      monthHeaders.push(`${MONTH_NAMES[m]} ${year}`)
    }
  }

  const headers = [
    'ID', 'Name', 'Description', 'Sub-Portfolio', 'Team', 'Tier', 'Status',
    'Due Date', 'Actual Go-Live', 'Delivered in Full',
    'Forecast Cost', 'Actual Cost',
    'Expected Value (Hrs)', 'Adoption Mode', 'Expected Users', 'Actual Users', 'Adoption Rate (%)',
    'Trade Spend Reduction', 'Growth',
    'Asana Project ID', 'Product ID',
    'Survey Sent At', 'Last Synced At', 'Last Meaningful Update',
    'Created By',
    ...monthHeaders,
  ]

  const rows = projects.map(p => {
    const monthValues: number[] = []
    for (const year of ledgerYears) {
      for (let m = 1; m <= 12; m++) {
        monthValues.push(monthTotal(p.monthlyLedger, monthKey(year, m)))
      }
    }
    return row([
      p.id,
      p.name,
      p.description ?? '',
      p.subPortfolio,
      p.team ?? '',
      p.tier,
      p.status,
      p.dueDate ?? '',
      p.actualGoLive ?? '',
      p.deliveredInFull ?? '',
      p.forecastCost,
      p.actualCost ?? '',
      p.expectedValue ?? '',
      p.adoptionMode ?? '',
      p.expectedUsers ?? '',
      p.actualUsers ?? '',
      p.adoptionRate ?? '',
      p.tradeSpendReduction ?? '',
      p.growth ?? '',
      p.asanaProjectId ?? '',
      p.productId ?? '',
      p.surveySentAt ?? '',
      p.lastSyncedAt ?? '',
      p.lastMeaningfulUpdate ?? '',
      p.createdBy?.name ?? '',
      ...monthValues,
    ])
  })

  const csv = [row(headers), ...rows].join('\n')
  download(csv, `KPI-All-Data-${dateStr}.csv`)
}

// Danone-standard risk register - one row per risk (all statuses) across the
// given projects, using the shared register's column names so the export can
// be pasted straight into the group template. "Risk" = impact x likelihood
// score (1-16); "Priority" = the RAG band. Legacy pre-matrix risks export
// their old severity in the Impact column with no score.
export function exportRiskRegisterCsv(projects: Project[]) {
  const headers = [
    'NR', 'Date raised', 'Risk description', 'Project', 'Owner',
    'Impact', 'Probability', 'Risk', 'Priority',
    'Ability to handle the risk/Risk response', 'Deadline',
    'Remarks/history', 'Status',
  ]

  const rows: string[] = []
  let nr = 1
  for (const p of projects) {
    for (const r of p.risks ?? []) {
      const score = riskScore(r)
      const rag = riskRag(r)
      rows.push(row([
        nr++,
        r.createdAt.slice(0, 10),
        r.title,
        p.name,
        r.owner?.name ?? r.createdBy?.name ?? '',
        r.impact ? RISK_LEVEL_LABEL[r.impact] : (r.severity ?? ''),
        r.likelihood ? RISK_LEVEL_LABEL[r.likelihood] : '',
        score ?? '',
        rag ? RAG_LABEL[rag] : '',
        r.mitigation ?? '',
        r.deadline ?? '',
        r.note ?? '',
        r.status,
      ]))
    }
  }

  const csv = [row(headers), ...rows].join('\n')
  download(csv, `Risk-Register-${new Date().toISOString().split('T')[0]}.csv`)
}

// "na" is the not-applicable answer only for a question that offers one; in
// a written answer it is just what was typed.
const answerCell = (q: SurveyQuestion, value: SurveyAnswer | undefined) =>
  q.kind !== 'text' && value === 'na' ? q.naLabel || 'N/A' : value ?? ''

/** Text a spreadsheet would run as a formula (starting =, +, -, @, a tab or a
 *  line break) kept as text with a leading apostrophe. Survey answers come
 *  from a public page, so nothing in them may run when the file is opened. */
export const asText = (cell: unknown) => (typeof cell === 'string' && /^[=+\-@\t\r\n]/.test(cell) ? `'${cell}` : cell)

/** The survey export's rows, heading first, every cell safe to open. */
export function surveyResponseRows(round: SurveyRoundResults, submissions: SurveySubmission[]): unknown[][] {
  // A Board column only for a round sent to Board members.
  const board = Object.values(round.invitedBoard ?? {}).some(n => n > 0) || submissions.some(s => s.board)
  return [
    ['Submitted', 'Department', 'Partner team', ...(board ? ['Board'] : []), ...round.questions.map(q => q.title || q.text)],
    ...submissions.map(s => [
      s.submittedOn,
      placeName(round, slotOf(s)),
      partnerName(round, s.partnerId),
      ...(board ? [s.board ? 'Yes' : 'No'] : []),
      ...round.questions.map(q => answerCell(q, s.answers[q.id])),
    ]),
  ].map(cells => cells.map(asText))
}

/** Every answer to one survey round, a row per person: the department they
 *  answered from and its partner team, never who they are. */
export function exportSurveyResponsesCsv(typeName: string, round: SurveyRoundResults, submissions: SurveySubmission[]) {
  const lines = surveyResponseRows(round, submissions).map(row)
  download(lines.join('\n'), `${`${typeName} ${round.name}`.replace(/[^\w.-]+/g, '-')}.csv`)
}
